import { useEffect, useId, useRef, type ReactNode } from 'react';

const Kreuz = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

interface DialogProps {
  offen: boolean;
  titel: ReactNode;
  /** gross = Lightbox für Details und Formulare (mit Kopf, scrollbarem Inhalt und Fuß), sonst kleiner Dialog für Rückfragen */
  gross?: boolean;
  onSchliessen: () => void;
  children: ReactNode;
  fuss?: ReactNode;
  breite?: number;
  /** Beschriftung des Schließen-Knopfs (Kundenseiten auf Englisch); Standard „Schließen“ */
  schliessenText?: string;
}

/** Natives <dialog>: Fokusfalle, Escape und Hintergrund-Sperre liefert der Browser */
export function Dialog({ offen, titel, onSchliessen, children, fuss, breite, gross = false, schliessenText = 'Schließen' }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelId = useId();
  // Schließen von außen (offen=false) darf onSchliessen nicht erneut auslösen –
  // sonst überschreibt es z. B. eine Navigation, die gerade den nächsten Dialog öffnet
  const vonAussen = useRef(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) d.showModal();
    if (!offen && d.open) {
      vonAussen.current = true;
      d.close();
    }
  }, [offen]);

  return (
    <dialog
      ref={ref}
      className={gross ? 'dxl-dialog dxl-dialog--gross' : 'dxl-dialog'}
      style={{ width: `min(${breite ?? (gross ? 640 : 520)}px, calc(100vw - 32px))` }}
      aria-labelledby={titelId}
      onClose={() => {
        if (vonAussen.current) {
          vonAussen.current = false;
          return;
        }
        onSchliessen();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onSchliessen();
      }}
    >
      <header className="dxl-dialog__kopf">
        <h2 id={titelId}>{titel}</h2>
        <button type="button" className="dxl-dialog__x" onClick={onSchliessen} aria-label={schliessenText}>
          <Kreuz />
        </button>
      </header>
      <div className="dxl-dialog__inhalt">{children}</div>
      {fuss && <footer className="dxl-dialog__fuss">{fuss}</footer>}
    </dialog>
  );
}
